import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  BookOpen, 
  PlayCircle, 
  Lightbulb, 
  HelpCircle,
  CheckCircle,
  ArrowRight,
  Stethoscope,
  MessageSquare,
  Calculator,
  FileText,
  Search,
  Star,
  User,
  Settings,
  Zap,
  Heart
} from 'lucide-react';

export default function GuidePage() {
  const [activeTab, setActiveTab] = useState('prise-en-main');

  const tabs = [
    { id: 'prise-en-main', label: 'Prise en Main', icon: PlayCircle },
    { id: 'tutoriels', label: 'Tutoriels', icon: BookOpen },
    { id: 'cas-pratiques', label: 'Cas Pratiques', icon: Stethoscope },
    { id: 'faq', label: 'FAQ', icon: HelpCircle }
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="guide-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3 mb-8">
          <div className="flex items-center justify-center gap-3">
            <BookOpen className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent">
              Guide d'Utilisation UrgenceMed
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Votre assistant médical numérique - Tutoriels complets pour les professionnels de santé
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap gap-2 justify-center">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isActive 
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30' 
                    : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))] hover:text-cyan-400 border border-[hsl(var(--border))]'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="mt-6">
          {activeTab === 'prise-en-main' && <PriseEnMain />}
          {activeTab === 'tutoriels' && <Tutoriels />}
          {activeTab === 'cas-pratiques' && <CasPratiques />}
          {activeTab === 'faq' && <FAQ />}
        </div>
      </div>
    </div>
  );
}

// ===== PRISE EN MAIN =====
function PriseEnMain() {
  return (
    <div className="space-y-6">
      <Alert className="bg-cyan-500/10 border-cyan-500/30">
        <Lightbulb className="w-4 h-4 text-cyan-400" />
        <AlertDescription>
          <p className="font-semibold text-cyan-300 mb-1">Bienvenue sur UrgenceMed !</p>
          <p className="text-[hsl(var(--text-dim))]">
            Ce guide vous accompagne dans la découverte de votre nouvel assistant médical numérique. Prenez 5 minutes pour découvrir les bases.
          </p>
        </AlertDescription>
      </Alert>

      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cyan-300">
            <PlayCircle className="w-6 h-6" />
            Premiers Pas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="space-y-4">
            {/* Étape 1 */}
            <AccordionItem value="etape-1" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <div className="flex items-center gap-3">
                  <Badge className="bg-cyan-500 text-white">Étape 1</Badge>
                  <User className="w-5 h-5 text-cyan-400" />
                  <span className="font-semibold">Créer votre compte professionnel</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3 mt-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">1</div>
                    <div>
                      <p className="font-semibold text-[hsl(var(--text))]">Cliquez sur "Créer un compte"</p>
                      <p className="text-sm text-[hsl(var(--text-dim))]">Sur la page de connexion, cliquez sur le lien "Créer un compte"</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">2</div>
                    <div>
                      <p className="font-semibold text-[hsl(var(--text))]">Renseignez vos informations</p>
                      <ul className="text-sm text-[hsl(var(--text-dim))] list-disc list-inside ml-2 mt-1">
                        <li>Nom et prénom complets</li>
                        <li>Adresse email professionnelle</li>
                        <li>Mot de passe sécurisé (min. 8 caractères)</li>
                        <li>Profession : Médecin, Infirmier(ère) ou Interne</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs font-bold">3</div>
                    <div>
                      <p className="font-semibold text-[hsl(var(--text))]">Validez et découvrez l'abonnement</p>
                      <p className="text-sm text-[hsl(var(--text-dim))]">Après inscription, vous serez dirigé vers la page d'abonnement pour activer votre accès complet (7 jours d'essai gratuit inclus).</p>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded">
                    <p className="text-xs text-green-300 font-semibold">✓ Astuce : Utilisez votre email professionnel pour faciliter la récupération de compte</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Étape 2 */}
            <AccordionItem value="etape-2" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <div className="flex items-center gap-3">
                  <Badge className="bg-cyan-500 text-white">Étape 2</Badge>
                  <Settings className="w-5 h-5 text-cyan-400" />
                  <span className="font-semibold">Découvrir l'interface</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3 mt-3">
                  <p className="text-[hsl(var(--text-dim))]">Une fois connecté, voici les zones principales de l'application :</p>
                  
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="p-3 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded">
                      <p className="font-semibold text-cyan-300 mb-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                        Menu Latéral Gauche
                      </p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Navigation principale : Accueil, Médicaments, Protocoles, Chat IA, Outils</p>
                    </div>
                    
                    <div className="p-3 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded">
                      <p className="font-semibold text-cyan-300 mb-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                        Header en Haut
                      </p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Toggle Adulte/Pédiatrie, votre profil, accès admin (si applicable)</p>
                    </div>
                    
                    <div className="p-3 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded">
                      <p className="font-semibold text-cyan-300 mb-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                        Zone Centrale
                      </p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Contenu principal : base de données, calculateurs, protocoles</p>
                    </div>
                    
                    <div className="p-3 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded">
                      <p className="font-semibold text-cyan-300 mb-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                        Boutons Flottants
                      </p>
                      <p className="text-xs text-[hsl(var(--text-dim))]">Assistante vocale Julia et Chat IA instantané (coins de l'écran)</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Étape 3 */}
            <AccordionItem value="etape-3" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <div className="flex items-center gap-3">
                  <Badge className="bg-cyan-500 text-white">Étape 3</Badge>
                  <Zap className="w-5 h-5 text-cyan-400" />
                  <span className="font-semibold">Personnaliser votre expérience</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3 mt-3">
                  <div className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-[hsl(var(--text))]">Enregistrez vos favoris</p>
                      <p className="text-sm text-[hsl(var(--text-dim))]">Cliquez sur l'icône ⭐ sur chaque page pour ajouter des médicaments, protocoles ou outils à vos favoris. Retrouvez-les rapidement depuis votre profil.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-[hsl(var(--text))]">Complétez votre profil</p>
                      <p className="text-sm text-[hsl(var(--text-dim))]">Cliquez sur votre nom en haut à droite → Profil. Ajoutez votre spécialité et préférences pour une expérience personnalisée.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <Settings className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-[hsl(var(--text))]">Basculez entre Adulte et Pédiatrie</p>
                      <p className="text-sm text-[hsl(var(--text-dim))]">Utilisez le toggle en haut de l'écran pour adapter les contenus et calculateurs à votre patient (adulte ou enfant).</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}

// ===== TUTORIELS =====
function Tutoriels() {
  return (
    <div className="space-y-6">
      <Alert className="bg-blue-500/10 border-blue-500/30">
        <BookOpen className="w-4 h-4 text-blue-400" />
        <AlertDescription>
          <p className="font-semibold text-blue-300 mb-1">Tutoriels pas à pas</p>
          <p className="text-[hsl(var(--text-dim))]">
            Apprenez à utiliser chaque fonctionnalité de UrgenceMed avec des guides détaillés.
          </p>
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Tuto 1 - Chat IA */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300 text-lg">
              <MessageSquare className="w-5 h-5" />
              Chat IA Médical
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[hsl(var(--text-dim))]">Posez vos questions médicales à l'IA et obtenez des réponses contextualisées instantanément.</p>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Cliquez sur "Chat" dans le menu latéral</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Tapez votre question médicale dans la zone de texte</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Recevez une réponse détaillée en quelques secondes</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Affinez avec des questions de suivi</p>
              </div>
            </div>

            <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded text-xs">
              <p className="font-semibold text-cyan-300 mb-1">Exemple de question :</p>
              <p className="text-[hsl(var(--text-dim))]">"Quels sont les critères de gravité d'une pneumonie communautaire chez l'adulte ?"</p>
            </div>
          </CardContent>
        </Card>

        {/* Tuto 2 - Calculateurs */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300 text-lg">
              <Calculator className="w-5 h-5" />
              Calculateurs de Dose
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[hsl(var(--text-dim))]">Calculez rapidement les doses médicamenteuses adaptées à chaque patient.</p>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Naviguez vers "Calculateurs" ou un médicament spécifique</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Sélectionnez le mode (Adulte/Pédiatrie)</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Entrez le poids du patient</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Ajustez la posologie selon l'indication</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Le débit (ml/h) s'affiche automatiquement</p>
              </div>
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded text-xs">
              <p className="font-semibold text-green-300 mb-1">✓ Sécurité :</p>
              <p className="text-[hsl(var(--text-dim))]">Les calculs sont vérifiés mais restent sous votre responsabilité professionnelle.</p>
            </div>
          </CardContent>
        </Card>

        {/* Tuto 3 - Protocoles */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300 text-lg">
              <FileText className="w-5 h-5" />
              Protocoles d'Urgence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[hsl(var(--text-dim))]">Accédez instantanément aux protocoles de prise en charge validés.</p>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Cliquez sur "Protocoles" dans le menu</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Utilisez la barre de recherche ou parcourez par catégorie</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Ouvrez un protocole pour voir les étapes détaillées</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Consultez les accordéons pour chaque section</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Ajoutez aux favoris pour un accès rapide</p>
              </div>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded text-xs">
              <p className="font-semibold text-blue-300 mb-1">📋 Formats disponibles :</p>
              <p className="text-[hsl(var(--text-dim))]">Consultez en ligne ou exportez en PDF pour impression.</p>
            </div>
          </CardContent>
        </Card>

        {/* Tuto 4 - Recherche */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-cyan-500/50 transition-all">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300 text-lg">
              <Search className="w-5 h-5" />
              Recherche Rapide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[hsl(var(--text-dim))]">Trouvez n'importe quelle information en quelques secondes.</p>
            
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Utilisez la barre de recherche en haut de chaque section</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Tapez un mot-clé : nom de médicament, pathologie, score</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Les résultats s'affichent en temps réel</p>
              </div>
              <div className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[hsl(var(--text-dim))]">Cliquez sur un résultat pour accéder à la fiche complète</p>
              </div>
            </div>

            <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded text-xs">
              <p className="font-semibold text-purple-300 mb-1">💡 Astuce :</p>
              <p className="text-[hsl(var(--text-dim))]">La recherche fonctionne avec les noms génériques et commerciaux des médicaments.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ===== CAS PRATIQUES =====
function CasPratiques() {
  return (
    <div className="space-y-6">
      <Alert className="bg-amber-500/10 border-amber-500/30">
        <Stethoscope className="w-4 h-4 text-amber-400" />
        <AlertDescription>
          <p className="font-semibold text-amber-300 mb-1">Cas Cliniques Pratiques</p>
          <p className="text-[hsl(var(--text-dim))]">
            Découvrez comment UrgenceMed vous assiste dans des situations cliniques réelles.
          </p>
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {/* Cas 1 */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Badge className="bg-red-500">Urgence</Badge>
                <span>Cas n°1 : Choc septique</span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
              <p className="font-semibold text-[hsl(var(--text))] mb-2">📋 Situation clinique :</p>
              <p className="text-sm text-[hsl(var(--text-dim))]">
                Patient de 68 ans, 75 kg, admis pour suspicion de choc septique sur pneumopathie. 
                TA 85/50, FC 115, SpO2 92% sous 6L d'O2. Vous devez démarrer une noradrénaline.
              </p>
            </div>

            <div className="space-y-2">
              <p className="font-semibold text-cyan-300">🎯 Comment UrgenceMed vous aide :</p>
              
              <div className="flex items-start gap-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 1 : Calculateur de Noradrénaline</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Menu → Outils → Noradrénaline → Mode Adulte → Poids: 75 kg → Posologie: 0.5 µg/kg/min → 
                    Résultat affiché : <strong className="text-cyan-300">22.5 ml/h</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 2 : Protocole Choc Septique</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Menu → Protocoles → Choc Septique → Consultez les étapes : remplissage, antibiothérapie, 
                    surveillance, critères d'admission en réanimation
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 3 : Chat IA pour précisions</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Chat → "Quels antibiotiques en 1ère intention pour pneumopathie avec choc septique ?"
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
              <p className="text-xs text-green-300 font-semibold">
                ✅ Résultat : Prise en charge complète en moins de 2 minutes avec les doses exactes et le protocole validé.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cas 2 */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Badge className="bg-blue-500">Pédiatrie</Badge>
                <span>Cas n°2 : Crise d'asthme pédiatrique</span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
              <p className="font-semibold text-[hsl(var(--text))] mb-2">📋 Situation clinique :</p>
              <p className="text-sm text-[hsl(var(--text-dim))]">
                Enfant de 6 ans, 20 kg, crise d'asthme sévère. FR 45/min, SpO2 88% AA, wheezing bilatéral, 
                tirage sous-costal. Vous devez administrer un traitement d'urgence.
              </p>
            </div>

            <div className="space-y-2">
              <p className="font-semibold text-cyan-300">🎯 Comment UrgenceMed vous aide :</p>
              
              <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 1 : Toggle Pédiatrie</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Activez le mode "Pédiatrie" en haut de l'écran pour adapter tous les contenus
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 2 : Paramètres Enfant</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Outils → Paramètres & Matériel → Âge: 6 ans → Obtenez poids théorique, tailles de matériel, 
                    doses de référence adaptées
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 3 : Protocole Asthme Aigu</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Protocoles → Asthme Aigu Pédiatrique → Doses de salbutamol, corticoïdes, critères de sévérité
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
              <p className="text-xs text-green-300 font-semibold">
                ✅ Résultat : Traitement adapté à l'âge et au poids avec matériel approprié identifié.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Cas 3 */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <div className="flex items-start justify-between">
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Badge className="bg-amber-500">Diagnostic</Badge>
                <span>Cas n°3 : Douleur thoracique</span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded">
              <p className="font-semibold text-[hsl(var(--text))] mb-2">📋 Situation clinique :</p>
              <p className="text-sm text-[hsl(var(--text-dim))]">
                Homme de 55 ans, douleur thoracique rétrosternale depuis 30 min, irradiation au bras gauche, 
                sueurs. Vous suspectez un SCA et devez évaluer le risque.
              </p>
            </div>

            <div className="space-y-2">
              <p className="font-semibold text-cyan-300">🎯 Comment UrgenceMed vous aide :</p>
              
              <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 1 : Chat IA Aide au Diagnostic</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Chat → "Critères diagnostiques d'un SCA ST+ ? Quand faut-il activer la filière coronaire ?"
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 2 : Score de Risque</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Scores → GRACE Score → Calculez le risque de mortalité à 6 mois
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                <CheckCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[hsl(var(--text))] text-sm">Étape 3 : Protocole SCA</p>
                  <p className="text-xs text-[hsl(var(--text-dim))]">
                    Protocoles → SCA ST+ → Traitement antithrombotique, délais de reperfusion, critères d'admission
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded">
              <p className="text-xs text-green-300 font-semibold">
                ✅ Résultat : Décision diagnostique et thérapeutique éclairée avec protocole de prise en charge immédiate.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ===== FAQ =====
function FAQ() {
  return (
    <div className="space-y-6">
      <Alert className="bg-purple-500/10 border-purple-500/30">
        <HelpCircle className="w-4 h-4 text-purple-400" />
        <AlertDescription>
          <p className="font-semibold text-purple-300 mb-1">Questions Fréquemment Posées</p>
          <p className="text-[hsl(var(--text-dim))]">
            Retrouvez les réponses aux questions les plus courantes des professionnels de santé.
          </p>
        </AlertDescription>
      </Alert>

      <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cyan-300">
            <HelpCircle className="w-6 h-6" />
            Foire Aux Questions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {/* Question 1 */}
            <AccordionItem value="q1" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  UrgenceMed est-il un dispositif médical ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                  <p className="text-sm text-[hsl(var(--text-dim))]">
                    <strong className="text-amber-300">Non</strong>, UrgenceMed est un outil d'aide à la décision clinique destiné aux professionnels de santé. 
                    Il ne remplace pas votre jugement médical ni ne constitue un diagnostic. Vous restez seul(e) responsable 
                    des décisions thérapeutiques prises pour vos patients.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Question 2 */}
            <AccordionItem value="q2" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  Comment fonctionne l'abonnement ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 space-y-2 text-sm text-[hsl(var(--text-dim))]">
                  <p>L'abonnement UrgenceMed Pro comprend :</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li><strong className="text-cyan-300">7 jours d'essai gratuit</strong> pour tester toutes les fonctionnalités</li>
                    <li>Tarif : <strong className="text-cyan-300">99,50 € par an</strong></li>
                    <li>Offre valable jusqu'au 5 janvier 2026 (après : 199 €/an)</li>
                    <li>Code promo disponible : <code className="bg-cyan-500/20 px-2 py-0.5 rounded text-cyan-300">YANIS50</code></li>
                    <li>Annulation possible à tout moment</li>
                    <li>Accès illimité à toutes les fonctionnalités</li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Question 3 */}
            <AccordionItem value="q3" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  Puis-je utiliser UrgenceMed hors connexion ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 text-sm text-[hsl(var(--text-dim))]">
                  <p className="mb-2">
                    UrgenceMed nécessite une connexion internet pour fonctionner car :
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Le Chat IA fonctionne en temps réel avec l'IA</li>
                    <li>La base de données est constamment mise à jour</li>
                    <li>Les protocoles sont synchronisés avec les dernières recommandations</li>
                  </ul>
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                    <p className="text-xs text-blue-300">
                      💡 <strong>Astuce :</strong> Ajoutez vos protocoles favoris pour un accès rapide, même avec une connexion limitée.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Question 4 */}
            <AccordionItem value="q4" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  Les données patient sont-elles stockées ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded text-sm">
                  <p className="font-semibold text-green-300 mb-2">✓ Confidentialité garantie</p>
                  <p className="text-[hsl(var(--text-dim))]">
                    <strong>Non</strong>, UrgenceMed ne stocke aucune donnée patient identifiable. Les calculs sont effectués 
                    en temps réel et aucune information médicale personnelle n'est conservée. Seules vos données de compte 
                    utilisateur (email, nom, profession) sont enregistrées conformément au RGPD.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Question 5 */}
            <AccordionItem value="q5" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  Comment sont validées les informations médicales ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 space-y-2 text-sm text-[hsl(var(--text-dim))]">
                  <p>Toutes les informations sont basées sur :</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Recommandations des sociétés savantes françaises (SFMU, SRLF, etc.)</li>
                    <li>Guidelines internationales (ESC, AHA, ERC)</li>
                    <li>Vidal et référentiels médicamenteux officiels</li>
                    <li>Revues médicales à comité de lecture</li>
                  </ul>
                  <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                    <p className="text-xs text-cyan-300">
                      📚 Les contenus sont régulièrement mis à jour pour refléter les dernières évolutions des pratiques.
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Question 6 */}
            <AccordionItem value="q6" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  Que faire si je rencontre un problème technique ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 space-y-3 text-sm">
                  <p className="text-[hsl(var(--text-dim))]">En cas de problème technique, suivez ces étapes :</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="text-cyan-400 font-bold">1.</span>
                      <p className="text-[hsl(var(--text-dim))]">Vérifiez votre connexion internet</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-cyan-400 font-bold">2.</span>
                      <p className="text-[hsl(var(--text-dim))]">Videz le cache de votre navigateur</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-cyan-400 font-bold">3.</span>
                      <p className="text-[hsl(var(--text-dim))]">Essayez de vous déconnecter puis reconnecter</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-cyan-400 font-bold">4.</span>
                      <div>
                        <p className="text-[hsl(var(--text-dim))]">Si le problème persiste, contactez le support :</p>
                        <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline flex items-center gap-2 mt-1">
                          <Heart className="w-4 h-4" />
                          support@openmeta.fr
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Question 7 */}
            <AccordionItem value="q7" className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]">
              <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))]">
                <span className="font-semibold text-[hsl(var(--text))] text-left">
                  Puis-je suggérer de nouvelles fonctionnalités ?
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded">
                  <p className="text-sm text-[hsl(var(--text-dim))]">
                    <strong className="text-purple-300">Absolument !</strong> Vos retours sont essentiels pour améliorer UrgenceMed. 
                    Envoyez vos suggestions, idées de protocoles ou fonctionnalités à :{' '}
                    <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline">support@openmeta.fr</a>
                    <br />
                    <br />
                    Nous étudions chaque demande et priorisons les fonctionnalités les plus demandées par la communauté.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
        <CardContent className="p-6">
          <div className="text-center space-y-3">
            <Heart className="w-10 h-10 text-cyan-400 mx-auto" />
            <h3 className="text-xl font-bold text-cyan-300">Besoin d'aide supplémentaire ?</h3>
            <p className="text-[hsl(var(--text-dim))] max-w-2xl mx-auto">
              Notre équipe de support est là pour vous accompagner. Contactez-nous pour toute question, suggestion ou problème technique.
            </p>
            <a 
              href="mailto:support@openmeta.fr"
              className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-semibold transition-colors"
            >
              <Heart className="w-4 h-4" />
              Contacter le Support
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
